import { apiClient } from '@/utils/api-client'

interface IntercomSettings {
  app_id: string
  api_base?: string
  intercom_user_jwt: string // JWT token containing all user data securely
}

class IntercomService {
  private booted: boolean = false
  private settings: IntercomSettings | null = null

  async initialize() {
    if (!import.meta.env.VITE_INTERCOM_APP_ID) {
      return
    }

    try {
      // Background call — skip the expired-session flow so an expected 401
      // can't trigger a re-login.
      const response = await apiClient.get<{ data: IntercomSettings }>('/api/users/intercom_settings', undefined, undefined, true)
      const settings = response.data || response
      const { app_id, api_base, intercom_user_jwt } = settings as IntercomSettings

      if (!app_id || !intercom_user_jwt) {
        return
      }

      this.settings = {
        app_id,
        api_base: api_base || "https://api-iam.intercom.io",
        intercom_user_jwt
      }

      this.boot()
    } catch (error) {
      console.error('Intercom: Failed to initialize:', error)
    }
  }

  boot() {
    if (!this.settings || this.booted) {
      return
    }

    window.Intercom('boot', this.settings)
    this.booted = true
  }

  update(settings?: Partial<IntercomSettings>) {
    if (!this.booted) return

    const updateSettings = settings || this.settings
    window.Intercom('update', updateSettings)
  }

  shutdown() {
    if (!this.booted) return

    window.Intercom('shutdown')
    this.booted = false
    this.settings = null
  }

  // Custom event tracking
  trackEvent(eventName: string, metadata?: Record<string, unknown>) {
    if (!this.booted) return

    window.Intercom('trackEvent', eventName, metadata)
  }

  // Show/hide messenger
  show() {
    if (!this.booted) return
    window.Intercom('show')
  }

  hide() {
    if (!this.booted) return
    window.Intercom('hide')
  }
}

export default new IntercomService()
